/**
 * CX Insights — KPI metrics only.
 *
 * Renders ONLY for role === 'cx'.
 * Backend: GET /api/role-insights/cx
 *
 * The actionable "My Listings" workflow table has moved to the Tasks tab
 * (see pages/tasks/CXTaskListings.jsx). This page now shows ONLY the
 * 8 KPI metric cards + offboarding cards.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ListChecks, Activity, CheckCircle2, Clock, AlertTriangle,
  Percent, Hourglass, Timer, Loader2, RefreshCw, Inbox,
} from 'lucide-react';
import { roleInsightsAPI } from '../../services/api';
import OffboardingInsightsCards from './OffboardingInsightsCards';
import DateRangeFilter, { rangeParams } from '../../components/DateRangeFilter';

const Card = ({ label, value, Icon, accent, testId, suffix, onClick }) => {
  const inner = (
    <>
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent.bg}`}>
        <Icon size={20} className={accent.fg} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-medium text-gray-500 truncate">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-0.5" data-testid={`${testId}-value`}>
          {value}{suffix && <span className="text-xs font-medium text-gray-500 ml-1">{suffix}</span>}
        </p>
      </div>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="text-left bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm hover:shadow-md hover:border-teal-200 transition w-full focus:outline-none focus:ring-2 focus:ring-teal-400"
        data-testid={testId}
      >
        {inner}
      </button>
    );
  }
  return (
    <div
      className="text-left bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm"
      data-testid={testId}
    >
      {inner}
    </div>
  );
};

const fmtMinutes = (mins) => {
  if (!mins || mins <= 0) return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
};

const CXInsights = ({ hideOffboardingCards = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [range, setRange] = useState('this_week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Preserve the active Insights date filter when deep-linking into Tasks.
  const gotoTasks = useCallback((view) => {
    const qp = new URLSearchParams({ view, ...rangeParams(range, customFrom, customTo) });
    navigate(`/tasks?${qp.toString()}`);
  }, [navigate, range, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await roleInsightsAPI.cx(rangeParams(range, customFrom, customTo));
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [range, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const cards = data?.cards || {};

  return (
    <div className="space-y-5" data-testid="cx-insights">
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">My CX Insights</h2>
          <p className="text-sm text-gray-500 mt-0.5">Your OTA listing performance at a glance. Work on listings from the <span className="font-medium text-teal-600">Tasks</span> tab.</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          data-testid="cx-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="cx-error">{error}</div>}

      <DateRangeFilter value={range} onChange={setRange} customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} testId="cx-insights-date-filter" />

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 size={24} className="animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="cx-cards-grid">
          <Card label="Assigned Listings" value={cards.assigned_listings ?? 0} Icon={ListChecks} accent={{ bg: 'bg-teal-50', fg: 'text-teal-600' }} testId="cx-card-assigned" onClick={() => gotoTasks('assigned')} />
          <Card label="Pending" value={cards.pending ?? 0} Icon={Inbox} accent={{ bg: 'bg-orange-50', fg: 'text-orange-600' }} testId="cx-card-pending" onClick={() => gotoTasks('assigned')} />
          <Card label="In Progress" value={cards.listing_in_progress ?? 0} Icon={Activity} accent={{ bg: 'bg-amber-50', fg: 'text-amber-600' }} testId="cx-card-in-progress" />
          <Card label="Completed" value={cards.completed_listings ?? 0} Icon={CheckCircle2} accent={{ bg: 'bg-emerald-50', fg: 'text-emerald-600' }} testId="cx-card-completed" onClick={() => gotoTasks('completed')} />
          <Card label="On-Time" value={cards.on_time_completed ?? 0} Icon={Clock} accent={{ bg: 'bg-sky-50', fg: 'text-sky-600' }} testId="cx-card-on-time" />
          <Card label="Late Completions" value={cards.late_completions ?? 0} Icon={AlertTriangle} accent={{ bg: 'bg-rose-50', fg: 'text-rose-600' }} testId="cx-card-late" />
          <Card label="On-Time Rate" value={cards.on_time_rate ?? 0} suffix="%" Icon={Percent} accent={{ bg: 'bg-indigo-50', fg: 'text-indigo-600' }} testId="cx-card-on-time-rate" />
          <Card label="Currently Overdue" value={cards.currently_overdue ?? 0} Icon={Hourglass} accent={{ bg: 'bg-red-50', fg: 'text-red-600' }} testId="cx-card-overdue" />
          <Card label="Avg Time" value={fmtMinutes(cards.average_completion_minutes)} Icon={Timer} accent={{ bg: 'bg-violet-50', fg: 'text-violet-600' }} testId="cx-card-avg" />
        </div>
      )}

      {/* Phase 16 — Offboarding cards (CX-scoped to ARI Freeze / Review / NOC with no-duplicate breakdown). */}
      {!hideOffboardingCards && <OffboardingInsightsCards />}
    </div>
  );
};

export default CXInsights;
